/**
 * The live Operations feed (the Operation / Category / Scope / Channel / Time /
 * Status table), sourced from the CRM Events `/live-events` endpoint and scoped to the
 * ACTIVE brand's tenant. Replaces the former mock websocket simulator with the
 * real fetch, keeping the `OperationRow[]` shape `OperationsFeed` renders.
 *
 * `/live-events` is a paged REST list (newest first), not a socket, so the "LIVE"
 * feed is approximated by a gentle poll: the first page is re-fetched on an
 * interval and the table replaced, so new operations surface on their own. The
 * poll pauses while the tab is hidden (no point hammering the foreign host in the
 * background) and resumes, with an immediate refresh, when it returns.
 *
 * The item shape is CONFIRMED against a tenant with live CRM data (see
 * `api/crm-events.ts`), and so is the status vocabulary the service actually
 * emits: `scheduled` / `sent` / `not_sent`. The item -> row mapping lives in
 * `api/live-event-map.ts` (unit-tested there); it reads every key defensively and
 * never throws, so a shape drift downgrades one cell rather than blanking the
 * feed. Contract: `docs/api.owners-hub.operations.md`.
 */
import React from 'react';

import { useAppSelector } from '@/store';

import type { OperationRow } from '../../mock-data/types';
import { listLiveEvents } from './api/crm-events';
import { toRows } from './api/live-event-map';

/**
 * Rows fetched per poll (newest first): one page of the live feed. Sized to give
 * the feed a real sample of the tenant's stream rather than a handful of rows,
 * which is also what gives it something to scroll through in both directions. The
 * panel caps its own height, so a bigger page does not stretch the page.
 */
const FEED_PAGE_SIZE = 100;

/** How often the live feed re-polls its first page (ms). */
const DEFAULT_POLL_MS = 15000;

export type OperationsLiveEventsResult = { rows: OperationRow[]; loading: boolean };

/**
 * @param pollMs Refresh cadence for the live feed (ms). Defaults to
 *        {@link DEFAULT_POLL_MS}; pass `0` to fetch once and not poll.
 */
export const useOperationsLiveEvents = (pollMs: number = DEFAULT_POLL_MS): OperationsLiveEventsResult => {
  const tenantId = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const [rows, setRows] = React.useState<OperationRow[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // No brand resolved yet: keep the feed loading until one does.
    if (!tenantId) {
      setLoading(true);
      setRows([]);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    let cancelled = false;

    // `initial` shows the skeleton; poll refreshes are silent (no loading flicker).
    const load = (initial: boolean): void => {
      if (initial) {
        setLoading(true);
        // These rows belong to the brand we are leaving. Dropping them here is
        // what stops a failed load leaving one brand's operations on screen
        // under another brand's name.
        setRows([]);
      }
      listLiveEvents({ tenantId, page: 1, pageSize: FEED_PAGE_SIZE }, signal)
        .then((response) => {
          if (cancelled) return;
          setRows(toRows(response.live_events.items, Date.now()));
          setLoading(false);
        })
        .catch(() => {
          // Ignore the abort the cleanup triggers; on a real failure just settle.
          // A POLL keeps whatever rows it had, so a blip doesn't blank the feed;
          // the initial load has already cleared them, since showing the previous
          // brand's stream would be worse than showing none.
          if (!cancelled && !signal.aborted) setLoading(false);
        });
    };

    load(true);

    if (pollMs <= 0) return () => controller.abort();

    const tick = (): void => {
      // Don't poll a backgrounded tab; the visibility handler refreshes on return.
      if (document.visibilityState === 'visible') load(false);
    };
    const timer = window.setInterval(tick, pollMs);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') load(false);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      controller.abort();
    };
  }, [tenantId, pollMs]);

  return { rows, loading };
};

export default useOperationsLiveEvents;
